import { useEffect, useState } from "react";
import ApiService from "../services/api.service";
import TokenService from "../services/token.service";

export const THEMES = [
  "light",
  "dark",
  "dark-contrast",
  "tech",
  "navy",
] as const;
export type ThemeName = (typeof THEMES)[number];

export const STORAGE_KEY = "ls_preferred_theme";
export const DEFAULT_THEME: ThemeName = "light";

export const isValidTheme = (value: unknown): value is ThemeName =>
  typeof value === "string" && (THEMES as readonly string[]).includes(value);

export const applyThemeClass = (theme: ThemeName) => {
  const root = document.documentElement;
  root.classList.remove(...THEMES);
  root.classList.add(theme);
};

export const readThemeFromDOM = (): ThemeName => {
  if (typeof document === "undefined") return DEFAULT_THEME;
  const cls = document.documentElement.classList;
  return (THEMES.find((t) => cls.contains(t)) ?? DEFAULT_THEME) as ThemeName;
};

export const useThemeSync = (): ThemeName => {
  const [theme, setTheme] = useState<ThemeName>(readThemeFromDOM);

  useEffect(() => {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (isValidTheme(cached)) applyThemeClass(cached);
    else applyThemeClass(DEFAULT_THEME);
    setTheme(readThemeFromDOM());

    let cancelled = false;
    const shouldLoadRemoteTheme =
      TokenService.isUserLoggedIn() &&
      window.location.pathname !== "/auth/sso-callback";
    if (shouldLoadRemoteTheme) {
      (async () => {
        try {
          const { data } = await ApiService.get({ url: "/accounts" });
          if (cancelled) return;
          const remote = data?.[0]?.preferredTheme ?? data?.preferredTheme;
          if (isValidTheme(remote)) {
            localStorage.setItem(STORAGE_KEY, remote);
            applyThemeClass(remote);
            setTheme(remote);
          }
        } catch {
          // not authenticated yet, or backend unreachable - keep cached/default
        }
      })();
    }

    // Other call sites (Settings page) flip the theme by mutating the root
    // class. Observe it so the React-side state stays in sync without those
    // sites needing to know about this hook.
    const observer = new MutationObserver(() => {
      const next = readThemeFromDOM();
      setTheme((prev) => (prev === next ? prev : next));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);

  return theme;
};
